import React, { useEffect, useRef, useState } from 'react';
import { z } from 'zod';
import { HardDriveDownload, Lock } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { ExportJobRecord, ExportFormatSchema } from '../../domains/export/models/ExportRequest';
import type { ExportQualityOption } from '../../hooks/export/useExportJob';
import { useEntitlements } from '../../hooks/useEntitlements';
import { usePlanValidation } from '../../hooks/usePlanValidation';
import { useExportProgress } from '../../hooks/export/useExportProgress';
import { useUpgradeModalStore } from '../../stores/upgradeModalStore';
import { OPEN_ONBOARDING_EVENT, Pages } from '../modals/OnboardingDialog';
import { Tracker } from '../../domains/analytics/services/Tracker';
import { SessionMetrics } from '../../domains/analytics/services/SessionMetrics';
import { CONVERSION_FUNNEL } from '../../domains/analytics/constants/MetricEvents';
import { toast } from 'sonner';

// Define ExportFormat type from the schema
export type ExportFormat = z.infer<typeof ExportFormatSchema>;

interface ExportFormatModalProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  onConfirm: (params: { format: ExportFormat; quality: ExportQualityOption; includeBranding?: boolean }) => Promise<void>;
  isSubmitting?: boolean;
  isSaving?: boolean;
  isDownloading?: boolean;
  error?: string | null;
  initialFormat?: ExportFormat;
  initialQuality?: ExportQualityOption;
  job?: ExportJobRecord | null;
  onCancel?: () => void;
  selectedShapeIds?: string[];
  onDownloadBackup?: () => void;
}


/**
 * Export selection modal presented inside the floating header. Handles format
 * selection, quality presets, and displays job progress/auto-download status.
 */
export const ExportFormatModal: React.FC<ExportFormatModalProps> = ({
  open,
  onOpenChange,
  onConfirm,
  isSubmitting = false,
  isSaving = false,
  isDownloading = false,
  error,
  initialFormat = 'png',
  initialQuality = 'normal',
  job,
  onCancel,
  selectedShapeIds,
  onDownloadBackup,
}) => {
  const { t } = useTranslation('diagram');
  const { t: tCommon } = useTranslation('common');
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat>(initialFormat);
  const [selectedQuality, setSelectedQuality] = useState<ExportQualityOption>(initialQuality);

  // Helper function to get format label
  const getFormatLabel = (format: ExportFormat): string => {
    return t(`export.${format}` as any);
  };

  // Helper function to get format description
  const getFormatDescription = (format: ExportFormat): string => {
    return t(`export.${format}Description` as any);
  };

  // Plan-based entitlements
  const { entitlements } = useEntitlements();
  const isFreePlan = !entitlements || entitlements.planType === 'free';
  const [includeBranding, setIncludeBranding] = useState(isFreePlan);
  const { validateExportFormat } = usePlanValidation();
  const { openModal: openUpgradeModal } = useUpgradeModalStore();
  const upgradeTimerRef = useRef<number | null>(null);

  const {
    progress,
    showProgress,
    statusMessage,
    isFailed,
    hasActiveExport,
  } = useExportProgress({ job, isSaving, isDownloading });

  useEffect(() => {
    if (open) {
      setSelectedFormat(initialFormat);
      setSelectedQuality(initialQuality);
      setIncludeBranding(isFreePlan);
      Tracker.track(CONVERSION_FUNNEL.EXPORT_MODAL_OPENED, { initialFormat });
    }
  }, [initialFormat, initialQuality, isFreePlan, open]);

  useEffect(() => () => {
    if (upgradeTimerRef.current !== null) {
      window.clearTimeout(upgradeTimerRef.current);
    }
  }, []);

  const handleConfirm = async () => {
    const validation = validateExportFormat(selectedFormat);

    if (!validation.allowed) {
      if (validation.requireSignIn) {
        Tracker.track('conversion.export.signin_required', { format: selectedFormat });
        onOpenChange(false);
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent(OPEN_ONBOARDING_EVENT, {
              detail: { page: Pages.SignIn },
            }),
          );
        }
        return;
      }
      if (validation.upgradeRequired) {
        onOpenChange(false);
        if (upgradeTimerRef.current !== null) {
          window.clearTimeout(upgradeTimerRef.current);
        }
        upgradeTimerRef.current = window.setTimeout(() => {
          openUpgradeModal(
            `export.${selectedFormat}`,
            t('export.formatNotAllowed', { format: selectedFormat })
          );
          upgradeTimerRef.current = null;
        }, 50);
      } else if (validation.reason) {
        toast.error(validation.reason);
      }
      return;
    }

    // Track format selected (conversion funnel)
    Tracker.track(CONVERSION_FUNNEL.EXPORT_FORMAT_SELECTED, { format: selectedFormat });
    
    // Track first-time export by format
    SessionMetrics.firstTime(`export.${selectedFormat}`);
    
    await onConfirm({ format: selectedFormat, quality: selectedQuality, includeBranding });
  };

  const showStartButton = !hasActiveExport;
  const isSvgFormat = selectedFormat === 'svg';
  const supportsQuality = selectedFormat === 'png' || selectedFormat === 'jpeg';
  const isQualityDisabled = isSvgFormat || !supportsQuality;
  const isBusy = isSubmitting || isSaving || isDownloading;

  const resolvedError = error || (isFailed ? statusMessage : null);
  
  const EXPORT_FORMATS: ExportFormat[] = ['png', 'jpeg', 'svg', 'pdf'];

  const qualityLabel = (q: ExportQualityOption): string => {
    if (q === 'low') return t('export.qualityLow');
    if (q === 'normal') return t('export.qualityNormal');
    return t('export.qualityHigh');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-[420px] z-[1001] flex flex-col h-[99dvh] sm:h-auto max-h-[99dvh] sm:max-h-[99vh] p-0 gap-0 overflow-hidden"
        overlayClassName="z-[1000]"
        data-testid="tid.export.format.dialog"
        onInteractOutside={(event) => {
          if (hasActiveExport) {
            event.preventDefault();
          }
        }}
      >
        <DialogHeader className="px-4 pt-4 pb-2 sm:px-6 sm:pt-6 sm:pb-3 shrink-0">
          <DialogTitle>
            {selectedShapeIds && selectedShapeIds.length > 0
              ? t('export.exportSelected', { count: selectedShapeIds.length })
              : t('export.exportDiagram')
            }
          </DialogTitle>
          <DialogDescription>
            {showProgress
              ? t('export.inProgress')
              : selectedShapeIds && selectedShapeIds.length > 0
                ? t('export.chooseFormatSelected', { count: selectedShapeIds.length })
                : t('export.chooseFormat')
            }
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-4 py-1 sm:px-6 sm:py-2">
          {!hasActiveExport && !isFailed && (
            <>
              <div className="space-y-1.5 sm:space-y-2">
                {EXPORT_FORMATS.map((format) => {
                  const isSelected = selectedFormat === format;
                  return (
                    <button
                      key={format}
                      type="button"
                      data-testid={`tid.export.format.${format}`}
                      onClick={() => {
                        if (!isBusy) {
                          setSelectedFormat(format);
                        }
                      }}
                      className={`w-full rounded-md border px-3 py-1 sm:py-1.5 text-left transition-colors ${
                        isSelected ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted'
                      }`}
                      disabled={isBusy}
                    >
                      <div className="text-sm font-medium text-foreground flex items-center justify-between">
                        {getFormatLabel(format)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {getFormatDescription(format)}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-1.5 sm:space-y-2 border-t pt-2 mt-2 sm:pt-3 sm:mt-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">{t('export.quality')}</span>
                  {isSvgFormat && (
                    <span className="text-xs text-muted-foreground">{t('export.qualityNote')}</span>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {(['low', 'normal', 'high'] as ExportQualityOption[]).map((quality) => {
                    const isSelected = selectedQuality === quality;
                    return (
                      <button
                        key={quality}
                        type="button"
                        data-testid={`tid.export.quality.${quality}`}
                        onClick={() => {
                          if (!isQualityDisabled) {
                            setSelectedQuality(quality);
                          }
                        }}
                        className={`rounded-md border px-2 py-1.5 text-sm transition-colors ${
                          isSelected ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted text-foreground'
                        } ${isQualityDisabled ? 'cursor-not-allowed opacity-50 hover:bg-transparent' : ''}`}
                        disabled={isBusy || isQualityDisabled}
                      >
                        {qualityLabel(quality)}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {t('export.qualityHelp')}
                </p>
              </div>

              {isFreePlan && (
                <div className="flex items-center justify-between border-t pt-2 mt-2 sm:pt-3 sm:mt-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-foreground">
                      {t('export.includeBranding', { defaultValue: 'Creately branding' })}
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded">
                      <Lock className="h-3 w-3" />
                      {t('export.pro', { defaultValue: 'PRO' })}
                    </span>
                  </div>
                  <Switch
                    data-testid="tid.export.branding"
                    checked={includeBranding}
                    onCheckedChange={(checked) => {
                      if (isFreePlan && !checked) {
                        onOpenChange(false);
                        if (upgradeTimerRef.current !== null) {
                          window.clearTimeout(upgradeTimerRef.current);
                        }
                        upgradeTimerRef.current = window.setTimeout(() => {
                          openUpgradeModal(
                            'export.branding',
                            t('export.brandingUpgradeRequired', {
                              defaultValue: 'Upgrade to remove Creately branding from exports',
                            })
                          );
                          upgradeTimerRef.current = null;
                        }, 50);
                        return;
                      }
                      setIncludeBranding(checked);
                    }}
                    disabled={isBusy}
                  />
                </div>
              )}

              {onDownloadBackup && (
                <div className="border-t pt-2 mt-2 sm:pt-3 sm:mt-3">
                  <button
                    type="button"
                    onClick={() => {
                      onDownloadBackup();
                      onOpenChange(false);
                    }}
                    className="w-full rounded-md border border-dashed border-border px-3 py-1 sm:py-1.5 text-left transition-colors hover:bg-muted"
                    disabled={isBusy}
                  >
                    <div className="text-sm font-medium text-foreground flex items-center gap-1.5">
                      <HardDriveDownload className="h-3.5 w-3.5" />
                      {t('export.yjsBackup')}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {t('export.yjsBackupDescription')}
                    </div>
                  </button>
                </div>
              )}
            </>
          )}

          {(showProgress || isFailed) && (
            <div className="py-2 sm:py-4 text-center">
              <div className="text-sm text-muted-foreground">
                {statusMessage}
              </div>
              {showProgress && !isFailed && (
                <div className="mt-2">
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-[width] duration-1000 ease-out"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {resolvedError && (
            <p className="mt-2 text-sm text-destructive" role="alert">
              {resolvedError}
            </p>
          )}
        </div>

        <DialogFooter className="px-4 pb-4 pt-2 sm:px-6 sm:pb-6 sm:pt-3 border-t shrink-0">
          <Button
            variant="ghost"
            onClick={() => {
              if (onCancel) {
                onCancel();
              } else {
                onOpenChange(false);
              }
            }}
          >
            {tCommon('actions.cancel')}
          </Button>
          {showStartButton && (
            <Button 
              data-testid="tid.export.start"
              data-event-click="export.start.click"
              data-event-params={JSON.stringify({ format: selectedFormat, quality: selectedQuality })}
              onClick={handleConfirm}
              disabled={isBusy}>
              {isSubmitting ? t('export.starting') : t('export.startExport')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      
    </Dialog>
  );
};
